import { createLazyLoadTrigger } from './lazyLoad';

export class ParticleWaveBackground {
  isLoaded = false;
  hasStartedBeforeInit = false;

  // Base (un-displaced) positions of particles
  basePositions = null;

  // Spring state (smoothed positions + velocities for elastic motion)
  currentPositions = null;
  velocities = null;

  // Mouse state in plane coordinates
  mouse = { x: 0, y: 0, isInside: false };

  // Cached pixel size so point size stays stable on resize
  particlePixelSize = null;

  constructor(containerElement, options = {}) {
    // Default configuration
    const baseConfig = {
      planeWidth: 10,
      planeHeight: 5,
      widthSegments: 120,
      heightSegments: 70,
      particleSize: 0.015,
      waveHeight: 0.5,
      noiseScale: 2,
      animationSpeed: 4000,
      gradient: [
        { color: [1, 1, 1, 1], stop: 0 },
        { color: [1, 1, 1, 0], stop: 100 },
      ],
      gradientOrigin: { x: 0, y: 0 },
      sizeScale: 0.5,
      sizeScaleOrigin: { x: 0, y: 0 },
      sizeScalePower: 1,
      rotation: { x: -Math.PI / 2.3, y: 0, z: 0 },
      cameraPosition: [0, 0, 5],
      cameraRotation: { x: 0, y: 0, z: 0 },
      autoStart: false,

      // Hover displacement config
      hoverRadius: 1, // in world units
      hoverStrength: 0.5, // how far particles are pushed away

      // Elastic hover animation (tune these to adjust feel)
      hoverSpringStiffness: 40, // higher = snappier attraction to target
      hoverSpringDamping: 10, // higher = less oscillation
    };

    const { breakpoints = null, ...restOptions } = options;

    this.defaultConfig = JSON.parse(JSON.stringify(baseConfig));
    this.breakpoints = breakpoints;

    this.baseConfig = {
      ...baseConfig,
      ...restOptions, // Override with user options
    };

    this.config = this.resolveBreakpointConfig(this.getViewportWidth());

    this.container = containerElement;

    // Bind handlers so we can add/remove listeners cleanly
    this.onMouseMove = this.handleMouseMove.bind(this);
    this.onMouseLeave = this.handleMouseLeave.bind(this);

    createLazyLoadTrigger({
      trigger: containerElement,
      start: 'top-=500 bottom',
      onLoad: () => this.init(),
      onError: (err) => {
        console.error('Failed to initialize particle wave background:', err);
      },
    });
  }

  normalizeColor(value) {
    if (Array.isArray(value)) {
      const r = Number.isFinite(value[0]) ? value[0] : 1;
      const g = Number.isFinite(value[1]) ? value[1] : 1;
      const b = Number.isFinite(value[2]) ? value[2] : 1;
      const a = Number.isFinite(value[3]) ? value[3] : 1;
      return [r, g, b, a];
    }

    if (typeof value === 'string') {
      const hex = value.trim().replace(/^#/, '');

      if (hex.length === 3 || hex.length === 4) {
        const r = parseInt(hex[0] + hex[0], 16);
        const g = parseInt(hex[1] + hex[1], 16);
        const b = parseInt(hex[2] + hex[2], 16);
        const a = hex.length === 4 ? parseInt(hex[3] + hex[3], 16) : 255;
        return [r / 255, g / 255, b / 255, a / 255];
      }

      if (hex.length === 6 || hex.length === 8) {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) : 255;
        return [r / 255, g / 255, b / 255, a / 255];
      }
    }

    return [1, 1, 1, 1];
  }

  async init() {
    const [THREE, { SimplexNoise }] = await Promise.all([
      import('three'),
      import('three/addons/math/SimplexNoise.js'),
    ]);

    this.THREE = THREE;
    this.SimplexNoise = SimplexNoise;

    this.config = this.resolveBreakpointConfig(this.getViewportWidth());
    this.setupScene();
    this.createParticles();
    if (this.config.autoStart) {
      this.startAnimation();
    }

    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(this.container);

    // Mouse listeners for hover effect (on window, so effect works even outside the parent)
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseleave', this.onMouseLeave);

    // Enable debug mode if URL has debug=true
    if (new URLSearchParams(window.location.search).has('debug')) {
      import('./AnimationDebugger.js').then(({ AnimationDebugger }) => {
        this.debugger = new AnimationDebugger(this, this.container);
      });
    }

    this.isLoaded = true;
    if (this.hasStartedBeforeInit) this.startAnimation();
  }

  setupScene() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.renderer = new this.THREE.WebGLRenderer({
      antialias: false,
      alpha: true,
    });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.container.appendChild(this.renderer.domElement);

    // Canvas styling
    const canvas = this.renderer.domElement;
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    // Keep pointer events off the canvas (events come from window)
    canvas.style.pointerEvents = 'none';

    this.scene = new this.THREE.Scene();
    this.camera = new this.THREE.PerspectiveCamera(
      45,
      width / height,
      0.1,
      1000,
    );
    this.updateCameraPosition();

    this.simplex = new this.SimplexNoise();

    this.circleTexture = this.createCircleTexture();

    // Raycaster for accurate mouse → world mapping
    this.raycaster = new this.THREE.Raycaster();
    this.ndcMouse = new this.THREE.Vector2();

    // Infinite plane helper aligned with the particles (used for fallback)
    this.interactionPlane = new this.THREE.Plane();
  }

  createParticles() {
    const geometry = new this.THREE.BufferGeometry();
    const pointsX = this.config.widthSegments + 1;
    const pointsY = this.config.heightSegments + 1;
    const pointCount = pointsX * pointsY;
    const positions = new Float32Array(pointCount * 3);
    const colors = new Float32Array(pointCount * 3);
    const alphas = new Float32Array(pointCount);
    const sizes = new Float32Array(pointCount);
    const halfW = this.config.planeWidth / 2;
    const halfH = this.config.planeHeight / 2;
    const sizeScaleOrigin = this.config.sizeScaleOrigin;
    const maxDistance = this.getMaxDistanceFromOrigin(sizeScaleOrigin);

    let idx = 0;
    for (let y = 0; y < pointsY; y++) {
      const tY = y / this.config.heightSegments;
      const posY = tY * this.config.planeHeight - halfH;
      for (let x = 0; x < pointsX; x++) {
        const tX = x / this.config.widthSegments;
        const posX = tX * this.config.planeWidth - halfW;

        positions[idx] = posX;
        positions[idx + 1] = posY;
        positions[idx + 2] = 0;
        sizes[idx / 3] = this.getSizeMultiplier(
          posX,
          posY,
          sizeScaleOrigin,
          maxDistance,
        );
        idx += 3;
      }
    }

    geometry.setAttribute(
      'position',
      new this.THREE.BufferAttribute(positions, 3),
    );
    geometry.setAttribute('color', new this.THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('alpha', new this.THREE.BufferAttribute(alphas, 1));
    geometry.setAttribute('size', new this.THREE.BufferAttribute(sizes, 1));

    this.particles = new this.THREE.Points(
      geometry,
      this.createParticleMaterial(),
    );

    this.particles.rotation.set(
      this.config.rotation.x,
      this.config.rotation.y,
      this.config.rotation.z,
    );
    this.scene.add(this.particles);
    this.particleGeometry = geometry;
    this.sizeAttribute = geometry.getAttribute('size');

    this.updateParticleSize({ initialize: this.particlePixelSize === null });

    // Store base (original) positions so displacement is non-cumulative
    this.basePositions =
      this.particleGeometry.attributes.position.array.slice();

    // Initialize spring state arrays
    this.currentPositions = new Float32Array(this.basePositions.length);
    this.currentPositions.set(this.basePositions);
    this.velocities = new Float32Array(this.basePositions.length); // starts at $0$

    // Invisible mesh aligned with the particle plane for local transforms
    this.intersectionPlane = new this.THREE.Mesh(
      new this.THREE.PlaneGeometry(
        this.config.planeWidth,
        this.config.planeHeight,
      ),
      new this.THREE.MeshBasicMaterial({ visible: false }),
    );
    this.intersectionPlane.position.copy(this.particles.position);
    this.intersectionPlane.rotation.copy(this.particles.rotation);
    this.scene.add(this.intersectionPlane);
  }

  getViewportWidth() {
    if (typeof window !== 'undefined' && window.innerWidth) {
      return window.innerWidth;
    }

    return this.container?.clientWidth ?? 0;
  }

  resolveBreakpointConfig(viewportWidth) {
    const overrides = this.getBreakpointOverrides(viewportWidth);
    const nextConfig = {
      ...this.baseConfig,
      ...overrides,
    };

    nextConfig.gradient = this.normalizeGradientStops(nextConfig.gradient);
    nextConfig.gradientOrigin = this.normalizeScaleOrigin(
      nextConfig.gradientOrigin,
    );
    nextConfig.sizeScaleOrigin = this.normalizeScaleOrigin(
      nextConfig.sizeScaleOrigin,
    );
    nextConfig.cameraRotation = this.normalizeRotation(
      nextConfig.cameraRotation,
    );
    nextConfig.rotation = this.normalizeRotation(nextConfig.rotation);

    return nextConfig;
  }

  normalizeScaleOrigin(origin) {
    if (!origin || typeof origin !== 'object') return { x: 0, y: 0 };

    return {
      x: Number.isFinite(origin.x) ? origin.x : 0,
      y: Number.isFinite(origin.y) ? origin.y : 0,
    };
  }

  normalizeRotation(rotation) {
    if (!rotation || typeof rotation !== 'object') {
      return { x: 0, y: 0, z: 0 };
    }

    return {
      x: Number.isFinite(rotation.x) ? rotation.x : 0,
      y: Number.isFinite(rotation.y) ? rotation.y : 0,
      z: Number.isFinite(rotation.z) ? rotation.z : 0,
    };
  }

  normalizeGradientStops(stops) {
    const fallback = [
      { color: [1, 1, 1, 1], stop: 0 },
      { color: [1, 1, 1, 0], stop: 100 },
    ];

    if (!Array.isArray(stops) || !stops.length) {
      return fallback.map((stop) => ({
        color: stop.color.slice(),
        stop: stop.stop,
      }));
    }

    const normalized = stops
      .map((stop) => {
        if (!stop || typeof stop !== 'object') return null;
        const stopValue = Number(stop.stop);
        if (!Number.isFinite(stopValue)) return null;

        return {
          color: this.normalizeColor(stop.color),
          stop: Math.min(Math.max(stopValue, 0), 100),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.stop - b.stop);

    if (!normalized.length) {
      return fallback.map((stop) => ({
        color: stop.color.slice(),
        stop: stop.stop,
      }));
    }

    if (normalized.length === 1) {
      const only = normalized[0];
      const duplicateStop = only.stop < 100 ? 100 : 0;
      normalized.push({ color: only.color.slice(), stop: duplicateStop });
      normalized.sort((a, b) => a.stop - b.stop);
    }

    return normalized;
  }

  sampleGradient(stops, tPercent) {
    if (!Array.isArray(stops) || !stops.length) return [1, 1, 1, 1];

    const t = Math.min(Math.max(tPercent, 0), 100);
    if (t <= stops[0].stop) return stops[0].color.slice();
    const last = stops[stops.length - 1];
    if (t >= last.stop) return last.color.slice();

    for (let i = 0; i < stops.length - 1; i++) {
      const start = stops[i];
      const end = stops[i + 1];
      if (t >= start.stop && t <= end.stop) {
        const span = Math.max(end.stop - start.stop, 0.0001);
        const localT = (t - start.stop) / span;
        return [
          start.color[0] + (end.color[0] - start.color[0]) * localT,
          start.color[1] + (end.color[1] - start.color[1]) * localT,
          start.color[2] + (end.color[2] - start.color[2]) * localT,
          start.color[3] + (end.color[3] - start.color[3]) * localT,
        ];
      }
    }

    return last.color.slice();
  }

  getBreakpointOverrides(viewportWidth) {
    if (!this.breakpoints || !viewportWidth) return {};

    const keys = Object.keys(this.breakpoints)
      .map((key) => Number(key))
      .filter((key) => !Number.isNaN(key))
      .sort((a, b) => a - b);

    let active = null;
    for (const key of keys) {
      if (viewportWidth >= key) active = key;
    }

    if (active === null) return {};

    return this.breakpoints[active] ?? this.breakpoints[String(active)] ?? {};
  }

  shouldRecreateParticles(prevConfig, nextConfig) {
    if (!prevConfig) return true;

    return (
      prevConfig.planeWidth !== nextConfig.planeWidth ||
      prevConfig.planeHeight !== nextConfig.planeHeight ||
      prevConfig.widthSegments !== nextConfig.widthSegments ||
      prevConfig.heightSegments !== nextConfig.heightSegments
    );
  }

  applyBreakpoints(viewportWidth) {
    const nextConfig = this.resolveBreakpointConfig(viewportWidth);
    const prevConfig = this.config;

    this.config = nextConfig;

    const geometryChanged = this.shouldRecreateParticles(
      prevConfig,
      nextConfig,
    );
    const particleSizeChanged =
      prevConfig?.particleSize !== nextConfig.particleSize;
    const particleRotationChanged =
      prevConfig?.rotation?.x !== nextConfig.rotation?.x ||
      prevConfig?.rotation?.y !== nextConfig.rotation?.y ||
      prevConfig?.rotation?.z !== nextConfig.rotation?.z;
    const sizeScaleChanged =
      prevConfig?.sizeScale !== nextConfig.sizeScale ||
      prevConfig?.sizeScalePower !== nextConfig.sizeScalePower ||
      prevConfig?.sizeScaleOrigin?.x !== nextConfig.sizeScaleOrigin?.x ||
      prevConfig?.sizeScaleOrigin?.y !== nextConfig.sizeScaleOrigin?.y;

    if (particleRotationChanged && this.particles) {
      this.particles.rotation.set(
        nextConfig.rotation.x,
        nextConfig.rotation.y,
        nextConfig.rotation.z,
      );
      if (this.intersectionPlane) {
        this.intersectionPlane.rotation.copy(this.particles.rotation);
      }
    }

    if (this.camera) {
      this.updateCameraPosition();
    }

    if (geometryChanged && this.particles) {
      this.particlePixelSize = null;
      this.recreateParticles();
    } else if (particleSizeChanged) {
      this.particlePixelSize = null;
      this.updateParticleSize({ initialize: true });
    } else if (sizeScaleChanged) {
      this.updateParticleScaleAttribute();
    }

    this.debugger?.refreshFromConfig?.(this.config);
  }

  getExportConfig() {
    const exportConfig = {
      ...this.baseConfig,
    };

    exportConfig.gradient = this.normalizeGradientStops(exportConfig.gradient);
    exportConfig.gradientOrigin = this.normalizeScaleOrigin(
      exportConfig.gradientOrigin,
    );
    exportConfig.cameraRotation = this.normalizeRotation(
      exportConfig.cameraRotation,
    );
    exportConfig.rotation = this.normalizeRotation(exportConfig.rotation);

    if (this.breakpoints) {
      exportConfig.breakpoints = this.breakpoints;
    }

    return exportConfig;
  }

  startAnimation() {
    if (!this.isLoaded) {
      this.hasStartedBeforeInit = true;
      return;
    }

    if (this.animationId) return;

    let lastTime = 0;
    const animate = (time) => {
      this.animationId = requestAnimationFrame(animate);

      // Skip frames when tab isn't visible
      if (document.hidden) {
        lastTime = time;
        return;
      }

      // Throttle to ~30fps
      if (time - lastTime > 33) {
        const delta = (time - lastTime) / 1000; // seconds
        this.updateParticles(time, delta);
        this.renderer.render(this.scene, this.camera);
        lastTime = time;
      }
    };
    animate(0);
  }

  stopAnimation() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  // --- Elastic particle update with spring-like mouse displacement ---
  updateParticles(time, delta) {
    const positions = this.particleGeometry.attributes.position;
    const colors = this.particleGeometry.attributes.color;
    const alphas = this.particleGeometry.attributes.alpha;
    const base = this.basePositions;

    const gradientStops = this.config.gradient;
    const gradientOrigin = this.config.gradientOrigin;
    const mouseActive = this.mouse.isInside && this.config.hoverStrength > 0;
    const radius = this.config.hoverRadius;

    // Cap delta to avoid instability on big frame drops
    const dt = Math.min(delta || 0.016, 0.05);

    const k = this.config.hoverSpringStiffness;
    const d = this.config.hoverSpringDamping;

    const maxDistance = this.getMaxDistanceFromOrigin(gradientOrigin);

    for (let i = 0; i < positions.count; i++) {
      const idx = i * 3;

      // Base (un-displaced) position from geometry
      const baseX = base[idx];
      const baseY = base[idx + 1];

      // Start from base as the default target
      let targetX = baseX;
      let targetY = baseY;

      // Mouse-based displacement (repulsion from the cursor)
      if (mouseActive && radius > 0) {
        const dx = baseX - this.mouse.x;
        const dy = baseY - this.mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist > 0 && dist < radius) {
          const falloff = 1 - dist / radius; // $1$ at center → $0$ at radius
          const force = falloff * this.config.hoverStrength;

          targetX += (dx / dist) * force;
          targetY += (dy / dist) * force;
        }
      }

      // Spring dynamics toward targetX / targetY
      const curX = this.currentPositions[idx];
      const curY = this.currentPositions[idx + 1];
      const velX = this.velocities[idx];
      const velY = this.velocities[idx + 1];

      const ax = -k * (curX - targetX) - d * velX;
      const ay = -k * (curY - targetY) - d * velY;

      const newVelX = velX + ax * dt;
      const newVelY = velY + ay * dt;

      const newX = curX + newVelX * dt;
      const newY = curY + newVelY * dt;

      this.velocities[idx] = newVelX;
      this.velocities[idx + 1] = newVelY;
      this.currentPositions[idx] = newX;
      this.currentPositions[idx + 1] = newY;

      // Wave height based on base coordinates (so wave is stable)
      const z =
        this.config.waveHeight *
        this.simplex.noise3d(
          baseX / this.config.noiseScale,
          baseY / this.config.noiseScale,
          time / this.config.animationSpeed,
        );

      // Set displaced position (using spring-smoothed X/Y)
      positions.setXYZ(i, newX, newY, z);

      // Color based on radial distance from gradient origin (using base coords)
      const dx = baseX - gradientOrigin.x;
      const dy = baseY - gradientOrigin.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const t = Math.min(distance / maxDistance, 1) * 100;
      const [finalR, finalG, finalB, finalA] = this.sampleGradient(
        gradientStops,
        t,
      );

      colors.setXYZ(i, finalR, finalG, finalB);
      alphas.setX(i, finalA);
    }

    positions.needsUpdate = true;
    colors.needsUpdate = true;
    alphas.needsUpdate = true;
  }

  updateCameraPosition() {
    this.camera.position.set(...this.config.cameraPosition);
    this.camera.rotation.set(
      this.config.cameraRotation.x,
      this.config.cameraRotation.y,
      this.config.cameraRotation.z,
    );
    // this.camera.lookAt(0, 0, 0);
  }

  createParticleMaterial() {
    return new this.THREE.ShaderMaterial({
      transparent: true,
      blending: this.THREE.NormalBlending,
      alphaTest: 0.5,
      depthWrite: false,
      uniforms: {
        uMap: { value: this.circleTexture },
        uSize: { value: 1 },
      },
      vertexShader: `
        attribute vec3 color;
        attribute float alpha;
        attribute float size;
        varying vec3 vColor;
        varying float vAlpha;
        uniform float uSize;

        void main() {
          vColor = color;
          vAlpha = alpha;
          gl_PointSize = uSize * size;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D uMap;
        varying vec3 vColor;
        varying float vAlpha;

        void main() {
          vec4 tex = texture2D(uMap, gl_PointCoord);
          if (tex.a < 0.5) discard;
           gl_FragColor = vec4(vColor, vAlpha * tex);
        }
      `,
    });
  }

  calculateParticlePixelSize(height) {
    if (!height || !this.camera) return this.config.particleSize;

    const fov = this.camera.fov * (Math.PI / 180);
    const particleOrigin = this.particles?.position ?? new this.THREE.Vector3();
    const distance = Math.max(
      this.camera.position.distanceTo(particleOrigin),
      0.001,
    );
    const pixelsPerWorldUnit = height / (2 * Math.tan(fov / 2) * distance);

    return this.config.particleSize * pixelsPerWorldUnit;
  }

  updateParticleSize({ initialize = false } = {}) {
    if (!this.particles?.material || !this.renderer) return;

    if (initialize || this.particlePixelSize === null) {
      this.particlePixelSize = this.calculateParticlePixelSize(
        this.container.clientHeight,
      );
    }

    const pixelRatio = this.renderer.getPixelRatio();
    const size = this.particlePixelSize * pixelRatio;
    const material = this.particles.material;

    if (material.uniforms?.uSize) {
      material.uniforms.uSize.value = size;
    }
    material.needsUpdate = true;
  }

  updateParticleScaleAttribute() {
    if (!this.particleGeometry?.attributes?.size) return;

    const sizeScaleOrigin = this.config.sizeScaleOrigin;
    const maxDistance = this.getMaxDistanceFromOrigin(sizeScaleOrigin);
    const sizes = this.particleGeometry.attributes.size;
    const base = this.basePositions;
    if (!base) return;

    for (let i = 0; i < sizes.count; i++) {
      const idx = i * 3;
      const posX = base[idx];
      const posY = base[idx + 1];
      sizes.setX(
        i,
        this.getSizeMultiplier(posX, posY, sizeScaleOrigin, maxDistance),
      );
    }

    sizes.needsUpdate = true;
  }

  getMaxDistanceFromOrigin(origin) {
    const halfW = this.config.planeWidth / 2;
    const halfH = this.config.planeHeight / 2;
    const corners = [
      { x: -halfW, y: -halfH },
      { x: halfW, y: -halfH },
      { x: -halfW, y: halfH },
      { x: halfW, y: halfH },
    ];

    return corners.reduce((max, corner) => {
      const dx = corner.x - origin.x;
      const dy = corner.y - origin.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      return Math.max(max, dist);
    }, 0.0001);
  }

  getSizeMultiplier(posX, posY, origin, maxDistance) {
    const dx = posX - origin.x;
    const dy = posY - origin.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const t = Math.min(Math.max(dist / maxDistance, 0), 1);
    const power = this.config.sizeScalePower ?? 1;
    const sizeScale = this.config.sizeScale ?? 1;

    return 1 + (sizeScale - 1) * Math.pow(t, power);
  }

  createCircleTexture() {
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    const center = size / 2;
    const radius = size / 2;

    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(center, center, radius, 0, Math.PI * 2);
    ctx.fill();

    const texture = new this.THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  }

  onResize() {
    this.applyBreakpoints(this.getViewportWidth());
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(width, height);
    this.updateParticleSize();
  }

  recreateParticles() {
    // Remove old particles and plane
    if (this.particles) this.scene.remove(this.particles);
    if (this.intersectionPlane) this.scene.remove(this.intersectionPlane);

    this.particleGeometry?.dispose();
    this.particles?.material?.dispose();
    this.intersectionPlane?.geometry?.dispose();
    this.intersectionPlane?.material?.dispose();

    // Create new particles with updated geometry
    this.createParticles();
  }

  // --- Mouse handling with raycasting and smooth edge behavior ---

  handleMouseMove(event) {
    const canvas = this.renderer?.domElement;
    if (!canvas || !this.camera || !this.raycaster || !this.intersectionPlane) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const { clientX, clientY } = event;

    // Mouse in normalized device coordinates ($-1$ to $1$)
    const xNdc = ((clientX - rect.left) / rect.width) * 2 - 1;
    const yNdc = -((clientY - rect.top) / rect.height) * 2 + 1;

    this.ndcMouse.set(xNdc, yNdc);
    this.raycaster.setFromCamera(this.ndcMouse, this.camera);

    let worldPoint = null;

    // 1) Try intersection with the finite plane geometry
    const intersects = this.raycaster.intersectObject(
      this.intersectionPlane,
      false,
    );
    if (intersects.length) {
      worldPoint = intersects[0].point;
    } else {
      // 2) Fallback: intersect with the infinite plane aligned with the mesh
      this.intersectionPlane.updateWorldMatrix(true, false);

      const worldQuat = new this.THREE.Quaternion();
      this.intersectionPlane.getWorldQuaternion(worldQuat);

      const normal = new this.THREE.Vector3(0, 0, 1).applyQuaternion(worldQuat);
      const planePoint = this.intersectionPlane.getWorldPosition(
        new this.THREE.Vector3(),
      );

      this.interactionPlane.setFromNormalAndCoplanarPoint(normal, planePoint);

      worldPoint = new this.THREE.Vector3();
      if (
        !this.raycaster.ray.intersectPlane(this.interactionPlane, worldPoint)
      ) {
        // No intersection with the infinite plane (e.g., ray parallel)
        this.mouse.isInside = false;
        return;
      }
    }

    // Convert world → local plane coordinates (matches geometry coords)
    const localPoint = this.intersectionPlane.worldToLocal(worldPoint.clone());

    // Clamp to the visible plane rectangle for smooth behavior at edges
    const halfW = this.config.planeWidth / 2;
    const halfH = this.config.planeHeight / 2;

    this.mouse.x = Math.max(-halfW, Math.min(halfW, localPoint.x));
    this.mouse.y = Math.max(-halfH, Math.min(halfH, localPoint.y));

    this.mouse.isInside = true;
  }

  handleMouseLeave() {
    this.mouse.isInside = false;
  }

  dispose() {
    cancelAnimationFrame(this.animationId);
    this.animationId = null;

    this.resizeObserver?.disconnect();

    // Remove mouse listeners from window
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseleave', this.onMouseLeave);

    if (this.intersectionPlane) {
      this.scene.remove(this.intersectionPlane);
      this.intersectionPlane.geometry?.dispose();
      this.intersectionPlane.material?.dispose();
    }

    this.renderer?.dispose();
    this.particleGeometry?.dispose();
    this.particles?.material?.dispose();
    this.circleTexture?.dispose?.();
    this.debugger?.dispose?.();

    if (this.container && this.renderer) {
      this.container.removeChild(this.renderer.domElement);
    }

    this.basePositions = null;
    this.currentPositions = null;
    this.velocities = null;
    this.sizeAttribute = null;
  }
}
